import { Button, DatePicker, Form, Input, Select, Spin } from 'antd';
import moment from 'moment';
import { bool, func, object } from 'prop-types';
import { useEffect, useRef, useState } from 'react';

import { getInfo, getRegionsAndCities } from 'actions/general';

import { getMarket } from 'utils/helpers';
import { EditSVG } from 'components/iconsSVG/edit';
import { ChevronSVG } from 'components/iconsSVG/chevron';
import { AutoComplete, PhoneInput } from 'components/base';
import FormInput from 'components/formInput';
import { useI18n } from 'context/i18n';

import { getConfigByLocale } from 'utils/getConfigByLocale';

import { useValidate } from 'hooks';
import styles from './style.module.css';

const { Option } = Select;

const HomePickUp = ({ order: defaultOrder = {}, changeAddress, handleNext, startEmpty = false, onlyLatinAlphabet = false, onChangeDate = () => {} }) => {
  const { locale, t, localeConfigs } = useI18n();
  const validate = useValidate();
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [info, setInfo] = useState(null);
  const [modifiedAddress, setModifiedAddress] = useState(null);
  const init = useRef(false);

  const [HomePickUpForm] = Form.useForm();
  const [DateForm] = Form.useForm();
  const homePickUpRef = useRef(null);

  const localeFormat = getConfigByLocale(locale, localeConfigs);
  const [order] = useState(() => JSON.parse(JSON.stringify(defaultOrder)));
  const [pickUpData, setPickUpData] = useState(modifiedAddress || order?.receiver || order?.applicant);

  useEffect(() => {
    const getExtraInfoHandler = async () => {
      if (init.current || info !== null) return;
      const { phonePrefixes } = await getInfo({ profile: locale });
      const { cities, regions } = await getRegionsAndCities(locale);

      setInfo({
        phonePrefixes,
        cities,
        regions,
      });
      const newPickupData = JSON.parse(JSON.stringify({ ...pickUpData }));
      if (!newPickupData.regionCode) {
        const foundRegion = regions.find((region) => region.name === newPickupData.regionName);
        if (foundRegion) {
          newPickupData.regionCode = foundRegion.regionCode;
        }
      }

      if (!startEmpty) {
        changeAddress({
          ...newPickupData,
        });
      } else {
        setPickUpData((prev) => Object.fromEntries(Object.entries(prev).map(([key, value]) => [key, ''])));
        setIsEditing(true);
      }
      init.current = true;
    };

    getExtraInfoHandler();
  }, []);

  useEffect(() => {
    if (info !== null) {
      setIsLoading(false);
    }
  }, [info]);

  const handleFinishEdit = (values) => {
    // estos values tienen que tener estos valores nuevos

    const defaultData = order.receiver ?? order.applicant;
    const foundRegion = info.regions.find((region) => region.id === values.region);
    const newPickupData = {
      name: defaultData?.name || '',
      surname: defaultData?.surname || '',
      email: defaultData?.email || '',
      telephone: values.telephone,
      address: values.address,
      postalCode: values.postalCode,
      city: values.city,
      region: foundRegion ? `${foundRegion.id}-${foundRegion.name}` : values.region,
      regionCode: values.region,
      regionName: foundRegion?.name ?? values.region,
      country: values.country,
    };

    setModifiedAddress(newPickupData);
    setPickUpData(newPickupData);
    changeAddress({
      ...newPickupData,
      edited: true,
    });
    setIsEditing(false);
  };

  const setInvalidDate = () => {
    DateForm.setFields([
      {
        name: 'date',
        errors: [t('validation', 'invalid.date', 'Please, select a valid date')],
      },
    ]);
    return false;
  };

  // Can not select days before today and today
  const disabledDate = (current) => {
    let isDateDisabled = false;
    const today = moment();
    const tomorrow = today.add(1, 'days');
    const dayOfTheWeekForCurrent = current.day();

    if (current) {
      if (
        current < tomorrow.endOf('day') ||
        [0, 6].includes(dayOfTheWeekForCurrent) // weekend (6 sat, 0 sun)
      ) {
        isDateDisabled = true;
      }
    }

    return isDateDisabled;
  };

  const validateDate = () => {
    const realValue = DateForm.getFieldValue('date');

    if (realValue !== undefined) {
      const formattedValue = moment(realValue, localeFormat.dateFormat);

      if (formattedValue._isValid) {
        const isFutureDate = formattedValue.isAfter(new Date(), 'day');

        if (isFutureDate) {
          DateForm.setFields([
            {
              name: 'date',
              errors: [],
              value: formattedValue,
            },
          ]);

          onChangeDate();
          changeAddress((prev) => ({
            ...prev,
            returnDate: formattedValue.format('YYYY-MM-DD'),
            edited: modifiedAddress !== null,
          }));
          return true;
        }

        return setInvalidDate();
      }

      DateForm.setFields([
        {
          name: 'date',
          errors: [t('newsletter', 'error.fecha.incorrecta.registro')],
        },
      ]);
      return false;
    }

    return setInvalidDate();
  };

  const handleComplete = () => {
    let anyError = false;

    if (isEditing) {
      HomePickUpForm.getFieldsError();
    }

    const isDateValid = validateDate();

    if (!isDateValid) {
      anyError = true;
    }

    if (!anyError) {
      handleNext();
    }
  };

  // const regionIsNumeric = /^\d+$/.test(pickUpData.region);
  const currentRegion = info?.regions.find((item) => item.id === pickUpData.regionCode);
  const currentRegionName = currentRegion?.name ?? pickUpData.regionName;

  return (
    <Spin spinning={isLoading}>
      <span className={styles.divider}>
        <ChevronSVG />
      </span>
      <div className={styles.homePickUpTitleWrapper}>
        <h2 className={styles.homePickUpTitle}>{t('mis.pedidos', 'home.pick.up.information', '***Home Collection Information')}</h2>
        <p className={styles.homePickUpSubtitle}>{t('mis.pedidos', 'to.make.the.exchanging.process.the.easiest.for.you', '**')}</p>
      </div>

      {isEditing ?
        <Form
          form={HomePickUpForm}
          layout="vertical"
          ref={homePickUpRef}
          name="HomePickUp"
          onFinish={handleFinishEdit}
          requiredMark="*"
          scrollToFirstError
          autoComplete="true"
          validateTrigger={['onBlur']}
          className={styles.homePickUpForm}
          initialValues={{
            ...pickUpData,
            region: currentRegion?.id,
            preffix: getMarket(locale),
          }}
        >
          <FormInput
            name="address"
            rules={!onlyLatinAlphabet ? validate('address') : validate('addressLatinAlphabet')}
            floatLabel={t('compra.formulario', 'direccion')}
          >
            <Input placeholder="" />
          </FormInput>
          <FormInput name="country" rules={validate('country')} floatLabel={t('compra.formulario', 'pais')}>
            <Select autoComplete="country" disabled showArrow={false} virtual={false} dropdownClassName={styles.dropdown}>
              <Option value={getMarket(locale)}>{pickUpData.country}</Option>
            </Select>
          </FormInput>
          <FormInput name="region" rules={validate('region')} floatLabel={t('compra.formulario', 'provincia')}>
            <Select
              showSearch
              notFoundContent=""
              suffixIcon={<ChevronSVG />}
              virtual={false}
              dropdownClassName={styles.dropdown}
              options={info?.regions.map((item) => ({ value: item.id, label: item.name }))}
            ></Select>
          </FormInput>
          <FormInput rules={validate('postalCode')} name="postalCode" floatLabel={t('compra.formulario', 'cp')}>
            <Input placeholder="" />
          </FormInput>
          <FormInput name="city" rules={validate('cityOrder')} floatLabel={t('compra.formulario', 'ciudad')}>
            <AutoComplete
              inputValue={pickUpData.city}
              id="city-input"
              baseOptions={info !== null && info.cities ? info.cities.map((item) => ({ value: item })) : []}
              dropdownClassName={styles.dropdown}
            />
          </FormInput>
          <PhoneInput className={styles.phoneWrapper} info={info} />
          <Button className={styles.homePickUpSubmit} htmlType="submit">
            {t('compra.formulario', 'save.this.address', '**Save this address')}
          </Button>
        </Form>
      : <div className={styles.homePickUpResumeWrapper}>
          <div className={styles.homePickUpData}>
            <p>{pickUpData.address}</p>
            <p>
              <span>{pickUpData.postalCode}</span>
              <span>{pickUpData.city}</span>
            </p>
            <p>{currentRegionName}</p>
            <p>{pickUpData.country}</p>
            <p className={styles.homePickUpTelephone}>
              <span>
                {t('generico', 'telefono', '**Telephone')}: {pickUpData.telephone}
              </span>
            </p>
          </div>
          <div className={styles.homePickUpEditWrapper}>
            <Button
              className={styles.homePickUpEditButton}
              icon={<EditSVG />}
              onClick={() => {
                setIsEditing(true);
              }}
            />
          </div>
        </div>
      }

      <Form form={DateForm} onFinish={handleComplete} fields={['date']}>
        <p className={styles.collectionDateLabel}>{t('compra.formulario', 'pick.up.date')}</p>
        <div className={styles.dateForPickUpWrapper}>
          <FormInput name="date" required>
            <DatePicker onChange={validateDate} locale={localeFormat} format={localeFormat.dateFormat} onBlur={validateDate} disabledDate={disabledDate} />
          </FormInput>
        </div>
        <div className={styles.nextWrapper}>
          <Button type="primary" htmlType="submit" className={styles.nextStepBtn}>
            {t('pedido', 'pedido.devoluciones.siguiente.paso', 'Next')}
          </Button>
        </div>
      </Form>
    </Spin>
  );
};

HomePickUp.propTypes = {
  order: object,
  changeAddress: func,
  handleNext: func,
  withCodes: bool,
  startEmpty: bool,
  onlyLatinAlphabet: bool,
  onChangeDate: func,
};

export default HomePickUp;
